import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  ShieldCheck, Building2, Moon, Sun, Lock, User,
  ChevronRight, Wifi, Eye, EyeOff
} from 'lucide-react';
import { authApi } from '../utils/auth-api';
import { API_BASE } from '../utils/api-base';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

interface LoginScreenProps {
  onLogin: (session: any, client?: any) => void;
  onInitNeeded?: () => void;
}

type Portal = 'super-admin' | 'client';

export function LoginScreen({ onLogin, onInitNeeded }: LoginScreenProps) {
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const checkServer = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/health`, { method: 'GET' });
        setServerStatus(response.ok ? 'online' : 'offline');
      } catch {
        setServerStatus('offline');
      }
    };
    checkServer();
  }, []);

  const handleUnifiedLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Please enter username and password');
      return;
    }
    setLoading(true);
    try {
      const { session, client } = await authApi.unifiedLogin(loginForm.username, loginForm.password);
      
      if (session?.user?.role === 'super_admin' || session?.user?.role === 'admin_staff') {
        toast.success('Welcome back!');
        onLogin(session);
      } else {
        if (session.licenseExpired === true) {
          // No toast - the app will show LicenseExpiredScreen
        } else if (session.daysUntilExpiration !== undefined && session.daysUntilExpiration !== null && session.daysUntilExpiration <= 7 && session.daysUntilExpiration > 0) {
          toast.warning(`License expiring in ${session.daysUntilExpiration} day${session.daysUntilExpiration > 1 ? 's' : ''}. Please contact admin to renew.`, {
            duration: 10000,
          });
        } else {
          toast.success(`Welcome back, ${client?.name || 'User'}!`);
        }
        onLogin(session, client);
      }
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background overflow-hidden">

      {/* ── Left Panel ─────────────────────────────────────────── */}
      <motion.div
        initial={{ x: -60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="hidden lg:flex lg:w-[52%] relative flex-col items-center justify-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 45%, #312e81 100%)',
        }}
      >
        {/* Decorative orbs */}
        <motion.div
          className="absolute top-[-80px] left-[-80px] w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #6366f1, transparent)' }}
          animate={{ scale: [1, 1.15, 1], rotate: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-[-100px] right-[-50px] w-[500px] h-[500px] rounded-full opacity-[0.15]"
          style={{ background: 'radial-gradient(circle, #3b82f6, transparent)' }}
          animate={{ scale: [1.1, 1, 1.1], rotate: [0, -20, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Animated grid background */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), 
                              linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 text-center px-12 max-w-xl">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-8 mx-auto w-32 h-32 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center shadow-2xl"
          >
            <img src="/img/logo.png" alt="Factreon Logo" className="w-24 h-24 object-contain" />
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-5xl font-extrabold text-white mb-6 tracking-tight"
          >
            Factreon <span className="text-emerald-400"></span>
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-lg text-slate-300 leading-relaxed font-light"
          >
            Enterprise Energy Management System for real-time monitoring,
            automated reporting, and PLC-driven industrial analytics.
          </motion.p>

          <div className="mt-12 flex flex-wrap justify-center gap-6">
            {[
              { icon: ShieldCheck, text: 'Secure' },
              { icon: Wifi, text: 'Real-time' },
              { icon: Building2, text: 'Scalable' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + (i * 0.1) }}
                className="flex items-center gap-2 text-slate-400 text-sm bg-white/5 px-4 py-2 rounded-full border border-white/10 backdrop-blur-sm"
              >
                <item.icon className="w-4 h-4 text-emerald-400" />
                {item.text}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Right Panel (Login Form) ────────────────────────────── */}
      <div className="w-full lg:w-[48%] flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header & Logo for Mobile */}
          <div className="flex flex-col items-center lg:items-start mb-10">
            <div className="lg:hidden mb-6 w-20 h-20 flex items-center justify-center">
              <img src="/img/logo.png" alt="Factreon Logo" className="w-16 h-16 object-contain" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              Sign In
            </h2>
            <p className="text-muted-foreground mt-2">
              Welcome back to your energy management dashboard
            </p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key="unified-login"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <form onSubmit={handleUnifiedLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-username">Username or Email</Label>
                  <div className="relative group">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="login-username"
                      placeholder="Enter your username or email"
                      className="pl-10 h-11 bg-muted/50 border-transparent focus:bg-background transition-all"
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button type="button" className="text-xs text-primary hover:underline font-medium">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-11 h-11 bg-muted/50 border-transparent focus:bg-background transition-all"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-semibold text-[15px] gap-2 bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-700 shadow-lg shadow-primary/20 transition-all duration-300"
                  disabled={loading}
                >
                  {loading ? 'Authenticating...' : 'Sign in'}
                  <ChevronRight className="w-4 h-4" />
                </Button>


              </form>
            </motion.div>
          </AnimatePresence>

          {/* Footer Info */}
          <div className="mt-10 pt-10 border-t border-muted flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${serverStatus === 'online' ? 'bg-emerald-500 animate-pulse' : serverStatus === 'offline' ? 'bg-red-500' : 'bg-amber-500'}`} />
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                System: {serverStatus}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <p className="text-[11px] text-muted-foreground">© 2026 Factreon Corp</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
